'use client';

import React from 'react';
import { X } from 'lucide-react';
import { PANEL_HELP, HelpContent } from '@/lib/archimate/panel-help';

interface PanelHelpDialogProps {
  panelKey: string;
  onClose: () => void;
}

export function PanelHelpDialog({ panelKey, onClose }: PanelHelpDialogProps) {
  const helpContent = PANEL_HELP[panelKey];

  if (!helpContent) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-background rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-auto border border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-accent">{helpContent.title}</h3>
          <button
            onClick={onClose}
            className="text-foreground-tertiary hover:text-accent transition-colors"
            title="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="prose prose-invert prose-orange max-w-none">
          {helpContent.content.split('\n').map((paragraph, idx) => {
            if (paragraph.trim() === '') return null;

            // Handle bold markdown
            const parts = paragraph.split(/(\*\*.*?\*\*)/g);
            return (
              <p key={idx} className="text-foreground-secondary mb-3 leading-relaxed">
                {parts.map((part, i) => {
                  if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={i} className="text-foreground font-semibold">{part.slice(2, -2)}</strong>;
                  }
                  return part;
                })}
              </p>
            );
          })}
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Info Icon Button Component
 * Reusable button to trigger help dialog
 */
interface HelpIconButtonProps {
  onClick: () => void;
  className?: string;
}

export function HelpIconButton({ onClick, className = '' }: HelpIconButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`text-foreground-tertiary hover:text-accent transition-colors ${className}`}
      title="Learn how to use this feature"
      type="button"
    >
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    </button>
  );
}
